import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDirtyState } from "@/components/ui/entity-form";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { TemplateImpactDialog } from "@/features/catalog/components/template-impact-dialog";
import { useEditorExitGuard } from "@/features/catalog/lib/use-editor-exit-guard";
import {
	getAdminCategoryTemplate,
	listAdminCategories,
	previewSpecTemplate,
	type SpecTemplateImpact,
	upsertAdminCategoryTemplate,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { hasPermission } from "@/lib/permissions";

/**
 * The specification template for one category.
 *
 * WHAT THIS SCREEN CONTROLS. Plan 008 V2 requires that no attribute name is
 * hard-coded anywhere in this app: the product editor generates its
 * Specifications form from the template, so a new category needs a row here and
 * no code change. That makes this the screen the catalogue's extensibility
 * actually rests on -- and, until now, the only one with no way to reach it.
 * `POST .../template` had no caller.
 *
 * IT IS NOT A CRUD FORM, which is the part worth being careful about.
 * `upsertSpecTemplate` deletes and re-creates every attribute row on save, and
 * there is no rename. Products store `specifications` keyed by attribute NAME,
 * so correcting a typo strands every stored value under the old name -- not
 * deleted, just addressed by nothing. Marking an attribute required is
 * retroactive in the same silent way.
 *
 * So saving goes through the impact preview. The confirmation is not ceremony:
 * it is the only place those two consequences are visible, because this screen
 * shows a template and the damage lands on the catalogue underneath it.
 */

/** An attribute as this screen edits it. `id` is local, for list keys only. */
interface DraftAttribute {
	id: string;
	name: string;
	attributeType: string;
	isRequired: boolean;
	unit: string;
	enumValues: string;
}

/**
 * One attribute in the shape the server takes, for BOTH the preview and the
 * save. Named because those two must not diverge: the operator confirms counts
 * computed over this exact list, and the save writes this exact list.
 */
interface ProposedAttribute {
	name: string;
	attributeType: string;
	isRequired: boolean;
	displayOrder: number;
	unit?: string;
	enumValues?: string[];
}

const ATTRIBUTE_TYPES = ["text", "number", "enum", "boolean"] as const;

/**
 * A browse axis as this screen edits it.
 *
 * NAME, TYPE, AND -- FOR AN ENUM -- ITS ALLOWED VALUES. Nothing else, and the
 * omission is the design. The attribute rows directly above carry a "Required
 * before a product here can be published" checkbox, and copying that row
 * wholesale would add a control that persists nothing: `VariantAttributeDef` has
 * no `isRequired`, `validateVariantAttributes` never checks for a missing axis,
 * and the typed columns are checked only for pairing. Requiredness is not
 * enforced anywhere, so a checkbox here would promise a validation the server
 * does not perform.
 *
 * `boolean` is absent for the same kind of reason: the server's axis type enum
 * is `text | number | enum`, matching what a browse filter can offer.
 */
interface DraftAxis {
	id: string;
	name: string;
	type: string;
	enumValues: string;
}

const AXIS_TYPES = ["text", "number", "enum"] as const;

/**
 * Two, because there are two column pairs.
 *
 * `variant_axis_1_*` and `variant_axis_2_*` are what a variant stores and what
 * the browse filters read. A third axis has nowhere to go, and the variant
 * dialog refuses to offer one rather than writing it into the free-form bag
 * where no filter would see it. Refusing here as well means an operator finds
 * out on the screen that declares axes rather than on the screen that fills
 * them in.
 */
const MAX_BROWSE_AXES = 2;

/**
 * The two drafts as one value the baseline can be compared against.
 *
 * Outside the component because it closes over nothing -- defined inside, it was
 * a new function every render and therefore a `useMemo` dependency that would
 * have defeated the memoisation it sits in.
 */
/**
 * The two lists as the dirty check should see them.
 *
 * UNTOUCHED BLANK ROWS ARE DROPPED, and that is what stops the guard firing on a
 * screen nobody has typed into. A category with no template seeds its draft with
 * one blank attribute row -- otherwise the form is a heading and nothing else --
 * while the baseline is built from the loaded template and is therefore empty. So
 * the two never matched, and the editor was dirty from its first render. That is
 * the normal case rather than an edge: 5 of the 674 taxonomy categories carry a
 * template, and the tree's "Add specs" link points at the other 669.
 *
 * The cost of it was not the prompt. It is the same `window.confirm` that guards
 * the product editor's real unsaved work, and an operator who meets it daily on
 * a form they have not touched learns to dismiss it everywhere.
 *
 * Safe to drop: an unnamed attribute cannot be saved -- the editor's own blocker
 * refuses it -- so a row with nothing in it is not work an operator can lose.
 */
function comparable(rows: DraftAttribute[], axisRows: DraftAxis[]): unknown {
	return {
		attributes: rows.filter(
			(row) =>
				row.name.trim() !== "" ||
				row.unit.trim() !== "" ||
				row.enumValues.trim() !== "",
		),
		axes: axisRows.filter(
			(axis) => axis.name.trim() !== "" || axis.enumValues.trim() !== "",
		),
	};
}

let nextAxisId = 0;
function blankAxis(): DraftAxis {
	nextAxisId += 1;
	return { id: `axis-${nextAxisId}`, name: "", type: "text", enumValues: "" };
}

let nextLocalId = 0;
function blankAttribute(): DraftAttribute {
	nextLocalId += 1;
	return {
		id: `local-${nextLocalId}`,
		name: "",
		attributeType: "text",
		isRequired: false,
		unit: "",
		enumValues: "",
	};
}

export function SpecTemplateEditorPage() {
	const { categoryId = "" } = useParams();
	const queryClient = useQueryClient();
	const { operator } = useAdminAuthStore();
	const canEdit = hasPermission(operator, "admin:catalog:taxonomy");

	const [draft, setDraft] = useState<DraftAttribute[] | null>(null);
	/*
	 * Separate from `draft`, and null means "not seeded yet" for the same reason
	 * it does there. A category with no axes is a normal state and its correct
	 * initial rendering is an empty list with an Add button -- not a blank row,
	 * because unlike attributes a template needs no axes at all.
	 */
	const [axisDraft, setAxisDraft] = useState<DraftAxis[] | null>(null);
	/*
	 * The impact AND the exact proposal it was computed over.
	 *
	 * The save used to call `proposed()` again at confirm time, so an edit made
	 * while the dialog was open produced a save the operator had never been
	 * shown counts for -- the confirmation reading as a guard while guarding
	 * nothing. Freezing the proposal here makes that structurally impossible,
	 * which a focus trap on the dialog would not.
	 */
	const [confirming, setConfirming] = useState<{
		impact: SpecTemplateImpact;
		proposal: ProposedAttribute[];
		// Frozen alongside the attributes for the same reason: the save writes the
		// exact pair the operator was shown counts for.
		axisProposal: Array<{ name: string; type: string; enumValues?: string[] }>;
	} | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);

	const templateQuery = useQuery({
		queryKey: ["admin-catalog-template", categoryId],
		queryFn: () => getAdminCategoryTemplate(categoryId),
	});

	/*
	 * The category's NAME, from the list the tree already caches.
	 *
	 * A dedicated endpoint would be a third route for one string. This shares the
	 * tree's query key, so arriving from the tree costs nothing and arriving by
	 * URL fetches the taxonomy once.
	 */
	const categoriesQuery = useQuery({
		queryKey: ["admin-catalog-categories"],
		queryFn: listAdminCategories,
	});
	const category = categoriesQuery.data?.items.find(
		(row) => row.id === categoryId,
	);

	/*
	 * Seeded ONCE from the server, and only after it answers.
	 *
	 * `draft === null` means "not seeded yet" rather than "no attributes", which
	 * an empty array cannot express -- and a category with no template is exactly
	 * the case where the difference matters, since its correct initial state is
	 * one blank row rather than nothing to type into.
	 */
	if (draft === null && templateQuery.isSuccess) {
		const loaded = templateQuery.data?.attributes ?? [];
		setDraft(
			loaded.length > 0
				? loaded
						.slice()
						.sort((a, b) => a.displayOrder - b.displayOrder)
						.map((attribute) => ({
							id: attribute.id,
							name: attribute.name,
							attributeType: attribute.attributeType,
							isRequired: attribute.isRequired,
							unit: attribute.unit ?? "",
							enumValues: (attribute.enumValues ?? []).join(", "),
						}))
				: [blankAttribute()],
		);
	}

	if (axisDraft === null && templateQuery.isSuccess) {
		setAxisDraft(
			(templateQuery.data?.variantAttributes ?? []).map((axis, index) => ({
				id: `axis-loaded-${index}`,
				name: axis.name,
				type: axis.type,
				enumValues: (axis.enumValues ?? []).join(", "),
			})),
		);
	}

	const attributes = draft ?? [];
	const axes = axisDraft ?? [];

	/*
	 * THE THIRD EDITOR, and the one most worth guarding: it holds a whole page of
	 * typed work in its own draft state and had no protection at all.
	 *
	 * Compared by VALUE against the loaded template, using `useDirtyState`.
	 *
	 * The list `id`s are LEFT IN. An earlier version stripped them, on the theory
	 * that a freshly added row's `local-3` would not match a server id -- but both
	 * sides derive their ids from the same `templateQuery.data`, so they always
	 * compare equal, and a row the operator added changes the array's LENGTH,
	 * which is dirty either way. The stripping survived its mutant because there
	 * was nothing for it to do.
	 *
	 * Memoised on the query data for the reason the product editor documents: the
	 * hook re-seeds on identity, so a fresh object every render is an infinite
	 * loop rather than a slow path.
	 */
	const loadedTemplate = useMemo(
		() =>
			comparable(
				(templateQuery.data?.attributes ?? [])
					.slice()
					.sort((a, b) => a.displayOrder - b.displayOrder)
					.map((attribute) => ({
						id: attribute.id,
						name: attribute.name,
						attributeType: attribute.attributeType,
						isRequired: attribute.isRequired,
						unit: attribute.unit ?? "",
						enumValues: (attribute.enumValues ?? []).join(", "),
					})),
				(templateQuery.data?.variantAttributes ?? []).map((axis, index) => ({
					id: `axis-loaded-${index}`,
					name: axis.name,
					type: axis.type,
					enumValues: (axis.enumValues ?? []).join(", "),
				})),
			),
		[templateQuery.data],
	);

	const { isDirty: templateIsDirty } = useDirtyState(loadedTemplate);
	/*
	 * `draft === null` means the form has not seeded yet, which is not an edit.
	 * Without that check the guard fires on a screen the operator has not typed
	 * into -- the empty draft compares unequal to a template that has attributes.
	 *
	 * That covers the window BEFORE seeding only. The window after it, where a
	 * template-less category's seeded blank row compares unequal to an empty
	 * baseline, is `comparable`'s to close.
	 */
	useEditorExitGuard(
		draft !== null && templateIsDirty(comparable(attributes, axes)),
	);

	function updateAxis(id: string, patch: Partial<DraftAxis>): void {
		setAxisDraft((current) =>
			(current ?? []).map((axis) =>
				axis.id === id ? { ...axis, ...patch } : axis,
			),
		);
	}

	/** The axes in the shape both the preview and the save take. */
	function proposedAxes(): Array<{
		name: string;
		type: string;
		enumValues?: string[];
	}> {
		return axes
			.filter((axis) => axis.name.trim() !== "")
			.map((axis) => ({
				name: axis.name.trim(),
				type: axis.type,
				...(axis.type === "enum"
					? {
							enumValues: axis.enumValues
								.split(",")
								.map((value) => value.trim())
								.filter(Boolean),
						}
					: {}),
			}));
	}

	function update(id: string, patch: Partial<DraftAttribute>): void {
		setDraft((current) =>
			(current ?? []).map((attribute) =>
				attribute.id === id ? { ...attribute, ...patch } : attribute,
			),
		);
	}

	function move(index: number, by: number): void {
		setDraft((current) => {
			const next = [...(current ?? [])];
			const target = index + by;
			if (target < 0 || target >= next.length) return next;
			[next[index], next[target]] = [next[target], next[index]];
			return next;
		});
	}

	/** The proposal, in the shape both the preview and the save want. */
	function proposed(): ProposedAttribute[] {
		return attributes
			.filter((attribute) => attribute.name.trim() !== "")
			.map((attribute, index) => ({
				name: attribute.name.trim(),
				attributeType: attribute.attributeType,
				isRequired: attribute.isRequired,
				displayOrder: index,
				...(attribute.unit.trim() ? { unit: attribute.unit.trim() } : {}),
				...(attribute.attributeType === "enum"
					? {
							enumValues: attribute.enumValues
								.split(",")
								.map((value) => value.trim())
								.filter(Boolean),
						}
					: {}),
			}));
	}

	const preview = useMutation({
		/*
		 * The proposal is captured HERE and carried to the save, rather than
		 * re-derived at confirm time. `enumValues` goes with it: narrowing an
		 * enum strands every product holding a dropped value, and the impact
		 * cannot see that without the list.
		 */
		mutationFn: async () => {
			const proposal = proposed();
			/*
			 * The axes go WITH the proposal and are frozen alongside it, for the
			 * reason above: the operator confirms counts computed over this exact
			 * pair, and the save writes this exact pair. Re-deriving either at
			 * confirm time is the bug the freezing exists to make impossible.
			 */
			const axisProposal = proposedAxes();
			const impact = await previewSpecTemplate(
				categoryId,
				proposal.map(({ name, attributeType, isRequired, enumValues }) => ({
					name,
					attributeType,
					isRequired,
					...(enumValues ? { enumValues } : {}),
				})),
				axisProposal,
			);
			return { impact, proposal, axisProposal };
		},
		onSuccess: (result) => {
			setSaveError(null);
			setConfirming(result);
		},
	});

	const save = useMutation({
		mutationFn: () =>
			upsertAdminCategoryTemplate(categoryId, {
				// The proposal the operator was shown counts for, not whatever
				// the draft says now.
				attributes: confirming?.proposal ?? [],
				// The axes the operator was shown counts for. This used to pass the
				// LOADED template's axes straight back, which round-tripped them
				// untouched and is why declaring one needed an API call.
				variantAttributes: confirming?.axisProposal ?? [],
			}),
		onSuccess: async () => {
			setConfirming(null);
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-template", categoryId],
			});
			// The tree shows which categories carry a template; creating the first
			// one changes that row.
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-categories"],
			});
			/*
			 * RESEED, because freezing the proposal at preview time inverted the bug
			 * rather than removing it. The save now writes what was previewed --
			 * correct -- but an edit made behind the open dialog stays on screen
			 * afterwards, so the form shows text that was never written and the next
			 * save silently starts from it. Silent either way; this is the half that
			 * is still fixable here.
			 *
			 * AFTER both awaits, not before: `setDraft(null)` re-arms the seeding
			 * effect, which reads whatever the cache holds at that moment. Reseeding
			 * before the refetch settles would seed from the pre-save cache and never
			 * run again. A refetch that FAILS leaves the query `success` with stale
			 * data, so the form can still show the pre-save template -- which is the
			 * server's last known answer rather than an unsent local edit, and the
			 * lesser of the two lies.
			 */
			setDraft(null);
		},
		onError: (error) =>
			setSaveError(apiErrorMessage(error, "Could not save the template.")),
	});

	const named = proposed();
	const duplicates = named.filter(
		(attribute, index) =>
			named.findIndex((other) => other.name === attribute.name) !== index,
	);
	const unnamed = attributes.filter(
		(attribute) => attribute.name.trim() === "",
	);
	/*
	 * WHAT THE SERVER ACTUALLY ENFORCES, which is less than an earlier version
	 * of this comment claimed.
	 *
	 * `createSpecTemplateSchema` requires at least one attribute. It does NOT
	 * reject duplicate names, and `spec_template_attributes` carries only a
	 * non-unique index on `template_id` -- so the duplicate check here is
	 * CLIENT-ONLY, and an API caller can still create two attributes sharing a
	 * name. That is worth a unique index and a schema-level refusal; both are
	 * schema changes and belong in their own commit.
	 *
	 * The unnamed check is new: `proposed()` silently drops rows with a blank
	 * name, so an operator who typed a type and a unit but no name watched their
	 * row vanish from the save with nothing said.
	 */
	const namedAxes = axes.filter((axis) => axis.name.trim() !== "");
	const duplicateAxis = namedAxes.find(
		(axis, index) =>
			namedAxes.findIndex(
				(other) =>
					other.name.trim().toLowerCase() === axis.name.trim().toLowerCase(),
			) !== index,
	);

	const blocker =
		named.length === 0
			? "A template needs at least one named attribute."
			: duplicates.length > 0
				? `Two attributes share the name "${duplicates[0].name}".`
				: unnamed.length > 0
					? "An attribute has no name. Name it or remove it."
					: namedAxes.length > MAX_BROWSE_AXES
						? `A category can have at most ${MAX_BROWSE_AXES} variant axes, because a variant stores ${MAX_BROWSE_AXES}. Remove one, or record it as a specification instead.`
						: duplicateAxis
							? `Two variant axes share the name "${duplicateAxis.name.trim()}".`
							: null;

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>
						Specifications for {category?.name ?? "this category"}
					</CardTitle>
					<CardDescription>
						These attributes become the Specifications form on every product
						filed here. Nothing about them is written in code, which is why a
						new category needs no developer.{" "}
						<Link className="underline" to="/catalog/categories">
							Back to the tree
						</Link>
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					{templateQuery.isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : templateQuery.isError ? (
						<p role="alert" className="text-sm text-red-700">
							{apiErrorMessage(
								templateQuery.error,
								"Could not load this template.",
							)}
						</p>
					) : (
						<>
							{attributes.map((attribute, index) => (
								<div
									key={attribute.id}
									className="grid gap-3 rounded-2xl border p-3 md:grid-cols-[2fr_1fr_1fr_auto]"
								>
									<div className="grid gap-1.5">
										<Label htmlFor={`name-${attribute.id}`}>Name</Label>
										<Input
											id={`name-${attribute.id}`}
											value={attribute.name}
											disabled={!canEdit}
											onChange={(event) =>
												update(attribute.id, { name: event.target.value })
											}
										/>
									</div>

									<div className="grid gap-1.5">
										<Label htmlFor={`type-${attribute.id}`}>Type</Label>
										<Select
											value={attribute.attributeType}
											disabled={!canEdit}
											onValueChange={(value) =>
												update(attribute.id, { attributeType: value })
											}
										>
											<SelectTrigger
												id={`type-${attribute.id}`}
												aria-label={`Type for ${attribute.name || "new attribute"}`}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectGroup>
													{ATTRIBUTE_TYPES.map((type) => (
														<SelectItem key={type} value={type}>
															{type}
														</SelectItem>
													))}
												</SelectGroup>
											</SelectContent>
										</Select>
									</div>

									<div className="grid gap-1.5">
										<Label htmlFor={`unit-${attribute.id}`}>Unit</Label>
										<Input
											id={`unit-${attribute.id}`}
											value={attribute.unit}
											disabled={!canEdit}
											placeholder="mm, %, kg"
											onChange={(event) =>
												update(attribute.id, { unit: event.target.value })
											}
										/>
									</div>

									<div className="flex items-end gap-2">
										<Button
											variant="ghost"
											size="sm"
											disabled={!canEdit || index === 0}
											aria-label={`Move ${attribute.name || "attribute"} up`}
											onClick={() => move(index, -1)}
										>
											Up
										</Button>
										<Button
											variant="ghost"
											size="sm"
											disabled={!canEdit || index === attributes.length - 1}
											aria-label={`Move ${attribute.name || "attribute"} down`}
											onClick={() => move(index, 1)}
										>
											Down
										</Button>
										<Button
											variant="ghost"
											size="sm"
											disabled={!canEdit}
											aria-label={`Remove ${attribute.name || "attribute"}`}
											onClick={() =>
												setDraft((current) =>
													(current ?? []).filter((a) => a.id !== attribute.id),
												)
											}
										>
											Remove
										</Button>
									</div>

									<label className="flex items-center gap-2 text-sm md:col-span-4">
										<input
											type="checkbox"
											checked={attribute.isRequired}
											disabled={!canEdit}
											onChange={(event) =>
												update(attribute.id, {
													isRequired: event.target.checked,
												})
											}
										/>
										Required before a product here can be published
									</label>

									{attribute.attributeType === "enum" ? (
										<div className="grid gap-1.5 md:col-span-4">
											<Label htmlFor={`values-${attribute.id}`}>
												Allowed values
											</Label>
											<Input
												id={`values-${attribute.id}`}
												value={attribute.enumValues}
												disabled={!canEdit}
												placeholder="Matte, Gloss, Satin"
												onChange={(event) =>
													update(attribute.id, {
														enumValues: event.target.value,
													})
												}
											/>
											<p className="text-xs text-muted-foreground">
												Separated by commas. These become the only options the
												product form offers.
											</p>
										</div>
									) : null}
								</div>
							))}

							<div className="grid gap-3 border-t pt-4">
								<div>
									<h3 className="text-sm font-semibold">Variant axes</h3>
									<p className="text-sm text-muted-foreground">
										What the same product varies along here — a finish, a size,
										a colour. These become the fields on a product's Variants
										tab, and the filters customers browse by. A category needs
										none unless its products come in more than one form.
									</p>
								</div>

								{axes.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										No axes. Products in this category are sold as themselves.
									</p>
								) : null}

								{axes.map((axis) => (
									<div
										className="grid gap-3 rounded-2xl border p-3 md:grid-cols-4"
										key={axis.id}
									>
										<div className="grid gap-1.5 md:col-span-2">
											<Label htmlFor={`axis-name-${axis.id}`}>Axis name</Label>
											<Input
												id={`axis-name-${axis.id}`}
												value={axis.name}
												disabled={!canEdit}
												placeholder="Finish"
												onChange={(event) =>
													updateAxis(axis.id, { name: event.target.value })
												}
											/>
										</div>

										<div className="grid gap-1.5">
											<Label htmlFor={`axis-type-${axis.id}`}>Axis type</Label>
											<select
												id={`axis-type-${axis.id}`}
												className="h-9 rounded-md border px-2 text-sm"
												value={axis.type}
												disabled={!canEdit}
												onChange={(event) =>
													updateAxis(axis.id, { type: event.target.value })
												}
											>
												{AXIS_TYPES.map((type) => (
													<option key={type} value={type}>
														{type}
													</option>
												))}
											</select>
										</div>

										<div className="flex items-end">
											<Button
												variant="ghost"
												size="sm"
												disabled={!canEdit}
												aria-label={`Remove ${axis.name || "axis"}`}
												onClick={() =>
													setAxisDraft((current) =>
														(current ?? []).filter((one) => one.id !== axis.id),
													)
												}
											>
												Remove
											</Button>
										</div>

										{axis.type === "enum" ? (
											<div className="grid gap-1.5 md:col-span-4">
												<Label htmlFor={`axis-values-${axis.id}`}>
													Allowed axis values
												</Label>
												<Input
													id={`axis-values-${axis.id}`}
													value={axis.enumValues}
													disabled={!canEdit}
													placeholder="Matte, Gloss, Satin"
													onChange={(event) =>
														updateAxis(axis.id, {
															enumValues: event.target.value,
														})
													}
												/>
												<p className="text-xs text-muted-foreground">
													Separated by commas. These become the only options the
													variant form offers.
												</p>
											</div>
										) : null}
									</div>
								))}

								{canEdit ? (
									<div>
										<Button
											variant="outline"
											size="sm"
											disabled={axes.length >= MAX_BROWSE_AXES}
											onClick={() =>
												setAxisDraft((current) => [
													...(current ?? []),
													blankAxis(),
												])
											}
										>
											Add axis
										</Button>
										{axes.length >= MAX_BROWSE_AXES ? (
											// Said, not merely enforced by a disabled button. A
											// control that stops working for no visible reason is
											// the one an operator clicks twice and reports as broken.
											<span className="ml-2 text-xs text-muted-foreground">
												A variant stores {MAX_BROWSE_AXES} axes, so a category
												can declare {MAX_BROWSE_AXES}.
											</span>
										) : null}
									</div>
								) : null}
							</div>

							{canEdit ? (
								<div className="flex items-center gap-2">
									<Button
										variant="outline"
										onClick={() =>
											setDraft((current) => [
												...(current ?? []),
												blankAttribute(),
											])
										}
									>
										Add attribute
									</Button>
									<Button
										disabled={blocker !== null || preview.isPending}
										onClick={() => preview.mutate()}
									>
										{preview.isPending ? "Checking..." : "Review and save"}
									</Button>
									{blocker ? (
										<span className="text-sm text-muted-foreground">
											{blocker}
										</span>
									) : null}
								</div>
							) : null}

							{preview.isError ? (
								<p role="alert" className="text-sm text-red-700">
									{apiErrorMessage(
										preview.error,
										"Could not work out what this change would do.",
									)}
								</p>
							) : null}
						</>
					)}
				</CardContent>
			</Card>

			{confirming ? (
				<TemplateImpactDialog
					categoryName={category?.name ?? "this category"}
					impact={confirming.impact}
					saving={save.isPending}
					error={saveError}
					onCancel={() => {
						setSaveError(null);
						setConfirming(null);
					}}
					onConfirm={() => save.mutate()}
				/>
			) : null}
		</div>
	);
}
